import {
	Dispatch,
	SetStateAction,
	useEffect,
	useMemo
} from "react";
import { useForceRerender } from "client/Hooks";
import {
	addTime,
	calculateTimeDifference,
	isBetween
} from "client/DateAndTime/dateTimeUtils";
import { CalendarAppointment, RegularCalendarAppointment } from "./CalendarPage";
import {
	CalendarEvent,
	RespondId,
	subscribe
} from "./events";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import { AppointmentResponseKind } from "client/Api/Calendar/calendar";
import { never } from "client_legacy/ndaw/errors";

export function useShouldShowMeetingButton(
	minutesBeforeShowing: number,
	start: Date,
	end: Date
): boolean {
	const [rerender] = useForceRerender();

	const showAt: Date = useMemo(
		(): Date => {
			return addTime(
				start,
				-minutesBeforeShowing,
				"minute"
			);
		},
		[minutesBeforeShowing, start]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			const timeoutHandle: SetTimeoutHandle = setTimeout(
				rerender,
				getNextCheckInterval(
					showAt,
					new Date(),
					end
				)
			);

			return () => clearTimeout(timeoutHandle);
		},
		[
			end,
			rerender,
			showAt
		]
	);

	return isBetween(
		new Date(),
		showAt,
		end
	);
};

const MS_PER_DAY: number = 1000 * 60 * 60 * 24;

function getNextCheckInterval(
	start: Date,
	now: Date,
	end: Date
): number {
	const msFromEnd: number = calculateTimeDifference(now, end);
	const msFromStart: number = calculateTimeDifference(now, start);
	if (msFromEnd >= 0) {
		return MS_PER_DAY;
	}

	if (msFromStart <= 0) {
		return Math.abs(msFromStart) + 1;
	}


	return Math.abs(msFromEnd) + 1 ;
}

export function useCalendarEvents(
	setAppointments: Dispatch<SetStateAction<readonly CalendarAppointment[]>>
): void {
	useEffect(
		(): (() => void) => {
			const subscriptionTokens: SubscriptionTokenCollection = registerSubscriptions(setAppointments);

			return (): void => subscriptionTokens.destroy();
		},
		[setAppointments]
	);
}

function registerSubscriptions(
	setAppointments: Dispatch<SetStateAction<readonly CalendarAppointment[]>>
): SubscriptionTokenCollection {
	const result = new SubscriptionTokenCollection();

	const respondedToken: PubSubSubscriptionToken = subscribe(
		CalendarEvent.Responded,
		(_, { id, response }): void => {
			setAppointments(
				(appointments) => applyResponse(
					appointments,
					id,
					response
				)
			);
		}
	);

	result.add(respondedToken);

	return result;
}

function applyResponse(
	appointments: readonly CalendarAppointment[],
	id: RespondId,
	response: AppointmentResponseKind.Accept | AppointmentResponseKind.Decline,
): readonly CalendarAppointment[] {
	const isAffected: (appointment: RegularCalendarAppointment) => boolean = "master" in id
		? (x) => x.masterId === id.master
		: (x) => x.id === id.appointment;

	const result: CalendarAppointment[] = [];

	for (const appointment of appointments) {
		if (appointment.kind === "regular" && isAffected(appointment)) {
			switch (response) {
				case AppointmentResponseKind.Accept:
					result.push({
						...appointment,
						myResponseStatus: {
							isResponseOutstanding: false,
							responseKind: response
						}
					});
					break;
				case AppointmentResponseKind.Decline:
					// Declined items are removed
					break;
				default:
					never(response);
			}
		} else {
			result.push(appointment);
		}
	}

	return result;
}
